/**
 * NotesSectionView - Displays player notes section with sharing support
 *
 * Renders a card containing player notes with a "+ Add" button. Adding and
 * editing a note both open the NoteEditorModal (SE-5519) instead of an inline
 * form — the modal overlays the section, so the list stays put underneath.
 * Note items show author name, date, Public badge, Shared badges, and content.
 * Author names display as "You" for current user's notes or the resolved
 * full name from Cognito attributes for other users.
 * Includes NoteShareSheet for sharing notes with teammates.
 *
 * The list body is drawn from a single read state (SE-7872): a failed read now
 * renders a retryable error instead of the same blank list a player with no
 * notes gets. The empty body is unchanged — "no notes yet" is a real answer
 * and stays legible; what changed is that a failure no longer borrows it.
 *
 * The "View All" overlay is hidden while the editor modal is open (and while the
 * share sheet is open) to avoid ambiguous nested-modal stacking; it restores
 * once the editor/share sheet closes because `showViewAllModal` is untouched.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { Plus, RefreshCwIcon, StickyNote } from "lucide-react-native";
import React, { memo } from "react";

import { Pressable } from "@/components/atoms/Pressable";

import type {
  PlayerNote,
  PlayerNotesReadState,
} from "@/features/player-detail/types";

import FullScreenOverlay from "@/components/organisms/shared/FullScreenOverlay";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import LabelPill from "@/components/molecules/LabelPill";
import PlayerDetailErrorState from "@/features/player-detail/components/molecules/PlayerDetailErrorState";

import NoteShareSheet from "@/features/notes/components/organisms/NoteShareSheet";
import NoteEditorModal, {
  type NoteEditorMode,
} from "@/features/player-detail/components/organisms/NoteEditorModal";
import NoteItem from "@/features/player-detail/components/organisms/NoteItem";
import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

/**
 * Props for NotesSectionView component
 */
interface NotesSectionViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Array of visible player notes (limited) */
  readonly notes: readonly PlayerNote[];
  /** Array of all player notes */
  readonly allNotes: readonly PlayerNote[];
  /** Total count of notes */
  readonly noteCount: number;
  /** Current note input value */
  readonly noteInput: string;
  /** Whether to show the View All modal */
  readonly showViewAllModal: boolean;
  /** Whether the add/edit editor modal is open */
  readonly isEditorOpen: boolean;
  /** Editor mode when open — add a new note or edit an existing one */
  readonly editorMode: NoteEditorMode;
  /** ID of the note currently being edited (null when adding) */
  readonly editingNoteId: string | null;
  /** Callback when note input changes */
  readonly onNoteInputChange: (text: string) => void;
  /** Callback when note is submitted */
  readonly onNoteSubmit: () => void;
  /** Callback when View all is pressed */
  readonly onViewAll: () => void;
  /** Callback when View All modal is closed */
  readonly onCloseViewAll: () => void;
  /** Callback when edit note is pressed */
  readonly onEditNote: (noteId: string) => void;
  /** Callback when delete note is pressed */
  readonly onDeleteNote: (noteId: string) => void;
  /** Callback to close/cancel the editor modal */
  readonly onCancelEdit: () => void;
  /** Set of note IDs owned by the current user */
  readonly ownedNoteIds: ReadonlySet<string>;
  /** Callback to open the add-note editor */
  readonly onStartAddNote: () => void;
  /** Whether the note is visible to others */
  readonly isPublic: boolean;
  /** Callback when visibility toggle changes */
  readonly onToggleVisibility: (value: boolean) => void;
  /** Callback when share note is pressed */
  readonly onShareNote: (noteId: string) => void;
  /** ID of the note currently being shared (null if not sharing) */
  readonly shareNoteId: string | null;
  /** Whether the share sheet is open */
  readonly isShareSheetOpen: boolean;
  /** Callback when the share sheet is closed */
  readonly onShareSheetClose: () => void;
  /** Callback when share state changes (after share/revoke) */
  readonly onShareChange: () => void;
  /** Whether note sharing is available (false when org defaults to public visibility) */
  readonly canShareNotes: boolean;
  /**
   * Which of the read's outcomes the list body should draw (SE-7872). The
   * count pill is suppressed for the two unsettled states — "0" next to a
   * failed read is the same false claim in miniature.
   */
  readonly readState: PlayerNotesReadState;
  /** Retries the notes read, from the error body or the staleness banner. */
  readonly onRetry: () => void;
  /**
   * Whether a retry is in flight.
   *
   * Drives the `stale` banner's Retry button. It does NOT reach the `failed`
   * body's CTA in practice, and the accurate claim is worth more here than the
   * tidy one: the container reads `notifyOnNetworkStatusChange`, so a retry
   * from `failed` turns `loading` true while the note count is still zero, and
   * the state becomes `loading` — the spinner replaces the error body before a
   * busy CTA could paint. That is SE-7580's third precedence clause working as
   * intended (in-flight beats a stale error, so a retry shows progress), not a
   * gap to plug. The prop is still passed down so the two bodies stay
   * symmetrical and so the CTA is correct if that precedence ever changes.
   *
   * The Scouting Reports card differs: its state is derived from a prop rather
   * than from a hook, so nothing flips it to `loading` mid-retry and the busy
   * CTA there does render.
   */
  readonly isRetrying: boolean;
  /**
   * Whether the card is one of two side-by-side columns rather than a block in
   * a stack. Only then may it take a zero flex basis — see
   * {@link CONTAINER_CLASS}.
   */
  readonly isSideBySide?: boolean;
}

/**
 * Card container surface (surface-card is correct in both light and dark).
 *
 * SE-6910: `flex-1` is applied only in the side-by-side layout, and this is a
 * correctness matter rather than a stylistic one. `flex-1` is
 * `flex: 1 1 0%` — a zero basis — so the card's box is sized by flex
 * distribution instead of by its own content. In the horizontal layout that is
 * exactly right: it is what makes the two columns share the row's width. In the
 * STACKED layout the main axis is vertical, so the same class makes the card
 * report a height its notes do not fit inside, and the overflow paints below
 * the box without lengthening the scroll extent. Measured on the web build at
 * 375x812 with four notes: the card laid out at 332.5px against 440px of
 * content, and the fourth note's edit and remove controls painted 107px past
 * where the scroll thought the content ended — underneath the floating action
 * button, with no scrolling left to move them out.
 */
const CONTAINER_CLASS =
  "rounded-lg border border-outline-card bg-surface-card p-4";

/** {@link CONTAINER_CLASS} plus the row-sharing basis, for the two-column layout. */
const SIDE_BY_SIDE_CONTAINER_CLASS = `flex-1 ${CONTAINER_CLASS}`;

/** Per-note divider row. */
const NOTE_ITEM_CLASS = "border-b border-border-outline-subtle py-3";

/** Copy for the `failed` body (SE-7872). */
const NOTES_ERROR_TITLE = "Couldn't load notes";
/**
 * Subtitle for the `failed` body.
 *
 * "That is not the same as there being none" is load-bearing, and is the same
 * sentence the Similar Players card uses (SE-7580): it is the whole reason
 * `failed` and `empty` are separate bodies rather than one blank list.
 */
const NOTES_ERROR_SUBTITLE =
  "We couldn't load this player's notes. That is not the same as there being none — try again.";
/** Copy for the `stale` body (notes shown, refresh failed). */
const NOTES_STALE_MESSAGE =
  "These notes may be out of date — the last refresh failed.";
/** Copy shown while the read is outstanding. */
const NOTES_LOADING_MESSAGE = "Loading notes…";

/** What {@link renderNotesBody} needs to draw any one of the five bodies. */
interface NotesBodyProps {
  /** The read's state, which decides which body renders. */
  readonly readState: PlayerNotesReadState;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Notes to draw, for the two states that have any. */
  readonly notes: readonly PlayerNote[];
  /** Set of note IDs owned by the current user */
  readonly ownedNoteIds: ReadonlySet<string>;
  /** Whether note sharing is available */
  readonly canShareNotes: boolean;
  /** Whether a retry is in flight */
  readonly isRetrying: boolean;
  /** Retries the read */
  readonly onRetry: () => void;
  /** Callback when edit note is pressed */
  readonly onEditNote: (noteId: string) => void;
  /** Callback when delete note is pressed */
  readonly onDeleteNote: (noteId: string) => void;
  /** Callback when share note is pressed */
  readonly onShareNote: (noteId: string) => void;
}

/**
 * Draws the note rows themselves.
 * @param props - Body props.
 * @returns The rows.
 */
const renderNoteRows = (props: NotesBodyProps) => (
  <Stack>
    {props.notes.map((note, index) => (
      <Box
        key={note.id}
        className={index < props.notes.length - 1 ? NOTE_ITEM_CLASS : "py-3"}
      >
        <NoteItem
          note={note}
          isDark={props.isDark}
          isOwned={props.ownedNoteIds.has(note.id)}
          onEditNote={props.onEditNote}
          onDeleteNote={props.onDeleteNote}
          onShareNote={props.onShareNote}
          canShareNotes={props.canShareNotes}
          previewLines={2}
        />
      </Box>
    ))}
  </Stack>
);

/**
 * Renders the list body for the given read state (SE-7872).
 *
 * Block-bodied on purpose — it is a five-way switch with an exhaustiveness
 * check, matching the shape the Similar Players list settled on. `empty`
 * deliberately returns the same bare rows container it always did: "this
 * player has no notes yet" is a real and common answer, and this change is
 * about stopping `failed` borrowing that answer, not about restyling it.
 * @param props - Body props.
 * @returns The list body.
 */
const renderNotesBody = (props: NotesBodyProps): React.ReactElement => {
  switch (props.readState) {
    case "loading":
      return (
        <Stack className="items-center justify-center gap-2 py-8">
          <Spinner size="small" />
          <Text variant="caption" className="text-content-muted">
            {NOTES_LOADING_MESSAGE}
          </Text>
        </Stack>
      );
    case "failed":
      return (
        <PlayerDetailErrorState
          title={NOTES_ERROR_TITLE}
          subtitle={NOTES_ERROR_SUBTITLE}
          onRetry={props.onRetry}
          isRetrying={props.isRetrying}
        />
      );
    case "stale":
      return (
        <Stack>
          <Box accessibilityRole="alert">
            <Stack
              direction="horizontal"
              className="items-center justify-between gap-2 rounded-sm bg-status-warning-surface px-3 py-2"
            >
              <Text
                variant="caption"
                className="shrink text-status-warning-strong"
              >
                {NOTES_STALE_MESSAGE}
              </Text>
              <Button
                label="Retry"
                tone="secondary"
                variant="outline"
                size="sm"
                labelTone="secondary"
                labelVariant="caption"
                icon={RefreshCwIcon}
                iconTone="secondary"
                isLoading={props.isRetrying}
                isDisabled={props.isRetrying}
                onPress={props.onRetry}
              />
            </Stack>
          </Box>
          {renderNoteRows(props)}
        </Stack>
      );
    case "ready":
    case "empty":
      return renderNoteRows(props);
    default: {
      const exhaustive: never = props.readState;
      return exhaustive;
    }
  }
};

/**
 * NotesSectionView renders the player notes section card with sharing support
 * @param props - View props containing notes data and callbacks
 * @param props.isDark - Whether dark mode is active
 * @param props.notes - Array of visible player notes
 * @param props.allNotes - Array of all player notes
 * @param props.noteCount - Total count of notes
 * @param props.noteInput - Current note input value
 * @param props.showViewAllModal - Whether to show the View All modal
 * @param props.isEditorOpen - Whether the add/edit editor modal is open
 * @param props.editorMode - Editor mode when open (add or edit)
 * @param props.editingNoteId - ID of note currently being edited
 * @param props.onNoteInputChange - Callback when note input changes
 * @param props.onNoteSubmit - Callback when note is submitted
 * @param props.onViewAll - Callback when View all is pressed
 * @param props.onCloseViewAll - Callback when View All modal is closed
 * @param props.onEditNote - Callback when edit note is pressed
 * @param props.onDeleteNote - Callback when delete note is pressed
 * @param props.onCancelEdit - Callback to close/cancel the editor modal
 * @param props.ownedNoteIds - Set of note IDs owned by the current user
 * @param props.onStartAddNote - Callback to open the add-note editor
 * @param props.isPublic - Whether the note is visible to others
 * @param props.onToggleVisibility - Callback when visibility toggle changes
 * @param props.onShareNote - Callback when share note is pressed
 * @param props.shareNoteId - ID of the note currently being shared
 * @param props.isShareSheetOpen - Whether the share sheet is open
 * @param props.onShareSheetClose - Callback when the share sheet is closed
 * @param props.onShareChange - Callback when share state changes
 * @param props.canShareNotes - Whether note sharing is available (false when org defaults to public visibility)
 * @param props.readState - Which read outcome the list body should draw (SE-7872)
 * @param props.onRetry - Retries the notes read from the error body or staleness banner
 * @param props.isRetrying - Whether a retry is in flight
 * @param props.isSideBySide - Whether the card is one of two side-by-side columns
 * @returns Box containing the notes section layout
 */
const NotesSectionView = ({
  isDark,
  notes,
  allNotes,
  noteCount,
  noteInput,
  showViewAllModal,
  isEditorOpen,
  editorMode,
  editingNoteId,
  onNoteInputChange,
  onNoteSubmit,
  onViewAll,
  onCloseViewAll,
  onEditNote,
  onDeleteNote,
  onCancelEdit,
  ownedNoteIds,
  onStartAddNote,
  isPublic,
  onToggleVisibility,
  onShareNote,
  shareNoteId,
  isShareSheetOpen,
  onShareSheetClose,
  onShareChange,
  canShareNotes,
  readState,
  onRetry,
  isRetrying,
  isSideBySide = false,
}: NotesSectionViewProps) => (
  <Box
    className={isSideBySide ? SIDE_BY_SIDE_CONTAINER_CLASS : CONTAINER_CLASS}
  >
    <Stack className="gap-3">
      {/* Header with icon, title, count, and view all link */}
      <Stack direction="horizontal" className="items-center justify-between">
        <Stack
          direction="horizontal"
          {...{ "data-testid": TEST_ID_PLAYER_DETAIL.NOTES_HEADING }}
          className="items-center gap-2"
        >
          <Icon as={StickyNote} size="sm" className="text-content-secondary" />
          <Text variant="title-sm" className="text-content-primary">
            Notes
          </Text>
          {/* SE-7872: a count is only shown once the read settled. Rendering
              "0" beside a failed or in-flight read asserts a number nobody
              measured — the same false claim the empty list used to make. */}
          {readState !== "loading" && readState !== "failed" && (
            <LabelPill label={String(noteCount)} tone="muted" />
          )}
        </Stack>
        {noteCount >= 1 && (
          <Pressable onPress={onViewAll}>
            <Text variant="body-strong" className="text-status-info">
              View all
            </Text>
          </Pressable>
        )}
      </Stack>

      {/* "+ Add" opens the add-note editor modal */}
      <Pressable
        testID={TEST_ID_PLAYER_DETAIL.ADD_NEW_NOTE_BUTTON}
        onPress={onStartAddNote}
        className="border-border-outline-default flex-row items-center gap-2 rounded-sm border bg-surface-muted px-3 py-2"
      >
        <Icon as={Plus} size="sm" className="text-content-secondary" />
        <Text variant="body" className="text-content-muted">
          Add
        </Text>
      </Pressable>

      {/* Notes list — or the loading / failed / stale body (SE-7872) */}
      {renderNotesBody({
        readState,
        isDark,
        notes,
        ownedNoteIds,
        canShareNotes,
        isRetrying,
        onRetry,
        onEditNote,
        onDeleteNote,
        onShareNote,
      })}

      {/* View All — hidden while the editor or share sheet is open to avoid
          ambiguous nested-modal stacking; restores when they close */}
      <FullScreenOverlay
        isOpen={showViewAllModal && !isShareSheetOpen && !isEditorOpen}
        onClose={onCloseViewAll}
        title="All Notes"
        desktopSize="md"
      >
        <Stack>
          {allNotes.map((note, index) => (
            <Box
              key={note.id}
              className={index < allNotes.length - 1 ? NOTE_ITEM_CLASS : "py-3"}
            >
              <NoteItem
                note={note}
                isDark={isDark}
                isOwned={ownedNoteIds.has(note.id)}
                onEditNote={onEditNote}
                onDeleteNote={onDeleteNote}
                onShareNote={onShareNote}
                canShareNotes={canShareNotes}
              />
            </Box>
          ))}
        </Stack>
      </FullScreenOverlay>

      {/* Add/Edit note editor modal — keyed on mode + note so each open
          starts from the container-seeded state (no reset effects) */}
      <NoteEditorModal
        key={`${editorMode}:${editingNoteId ?? "new"}`}
        isOpen={isEditorOpen}
        mode={editorMode}
        noteInput={noteInput}
        isPublic={isPublic}
        onNoteInputChange={onNoteInputChange}
        onToggleVisibility={onToggleVisibility}
        onSubmit={onNoteSubmit}
        onClose={onCancelEdit}
      />

      {/* Note share sheet */}
      <NoteShareSheet
        noteId={shareNoteId ?? ""}
        isOpen={isShareSheetOpen}
        onClose={onShareSheetClose}
        onShareChange={onShareChange}
      />
    </Stack>
  </Box>
);

NotesSectionView.displayName = "NotesSectionView";

export default memo(NotesSectionView);
